import { type RowData } from "@tanstack/react-table";
import { useModal } from "@ebay/nice-modal-react";
import { type AppTable } from "../../../components/table/tableFeatures";
import { successToast } from "../../../utils/toasts";
import { useDataViews } from "../api/useDataViews";
import { useCreateDataView } from "../api/useCreateDataView";
import { useUpdateDataView } from "../api/useUpdateDataView";
import { useDeleteDataView } from "../api/useDeleteDataView";
import {
  TABLE_VIEW_TYPE,
  applyTableStateToTable,
  isTableViewDirty,
  parseTableDataView,
  readTableStateFromTable,
  resetTableStateToDefaults,
} from "../viewTypes/table";
import { SaveDataViewModal, type SaveDataViewResult } from "../components/SaveDataViewModal";
import { ManageDataViewsModal } from "../components/ManageDataViewsModal";
import type { DataView, DataViewWriteBody } from "../../../zodios/apiDefinitions/api/dataViews";
import { useAppSelector } from "../../../redux/hooks";
import { useBuildScopeDefinition } from "./useBuildScopeDefinition";
import { useLocalStorage } from "@reactuses/core";

type UseDataViewOptions<TData extends RowData> = {
  viewKey: string;
  datasetKey: string;
  table: AppTable<TData>;
};

const activeIdStorageKey = (viewKey: string) => `dataView:${viewKey}:activeId`;

export const clearActiveDataViewId = (viewKey: string) => {
  const key = activeIdStorageKey(viewKey);
  localStorage.removeItem(key);
};

export const useDataView = <TData extends RowData>({ viewKey, datasetKey, table }: UseDataViewOptions<TData>) => {
  const [activeId, setActiveId] = useLocalStorage<number | null>(activeIdStorageKey(viewKey), null);

  const currentUserId = useAppSelector((state) => state.user.user_id);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const buildScopeDefinition = useBuildScopeDefinition();

  const saveModal = useModal(SaveDataViewModal);
  const manageModal = useModal(ManageDataViewsModal);

  const listQuery = useDataViews({ viewType: TABLE_VIEW_TYPE, datasetKey });

  const createMutation = useCreateDataView();
  const updateMutation = useUpdateDataView();
  const deleteMutation = useDeleteDataView();

  const views: readonly DataView[] = listQuery.data?.results ?? [];
  const activeView = views.find((v) => v.id === Number(activeId)) ?? null;
  const isDirty = activeView ? isTableViewDirty(readTableStateFromTable(table), parseTableDataView(activeView)) : false;

  const isOwner = (view: DataView) => String(view.created_by ?? "") === currentUserId;
  const myViews = views.filter(isOwner);
  const sharedViews = views.filter((v) => !isOwner(v));

  const setActiveView = (view: DataView | null) => {
    setActiveId(view?.id ?? null);
  };

  const apply = (view: DataView) => {
    applyTableStateToTable(table, parseTableDataView(view));
    setActiveView(view);
  };

  const revert = () => {
    if (!activeView) return;
    applyTableStateToTable(table, parseTableDataView(activeView));
    successToast({ message: "Discarded changes" });
  };

  const unapply = () => {
    resetTableStateToDefaults(table);
    setActiveView(null);
  };

  const buildBody = (overrides: Partial<DataViewWriteBody>): DataViewWriteBody => {
    const state = readTableStateFromTable(table);
    return {
      name: "",
      view_type: TABLE_VIEW_TYPE,
      dataset_key: datasetKey,
      fields: state.fields,
      filters: state.filters,
      grouping: state.grouping,
      sorting: state.sorting,
      search: state.search,
      marketplace: marketPlace,
      ...overrides,
    };
  };

  const saveAsNew = async () => {
    const result = (await saveModal.show({ existingNames: views.map((v) => v.name) })) as SaveDataViewResult | null;
    if (!result) return;
    createMutation.mutate(
      buildBody({
        name: result.name,
        description: result.description,
        scope_definition: buildScopeDefinition(result.scope, result.visibility),
      }),
      { onSuccess: setActiveView },
    );
  };

  const updateCurrent = () => {
    if (!activeView) return;
    const body = buildBody({
      name: activeView.name,
      description: activeView.description,
      dataset_key: activeView.dataset_key,
      scope_definition: activeView.scope_definition,
    });
    updateMutation.mutate({ id: activeView.id, body });
  };

  const remove = (view: DataView) => {
    deleteMutation.mutate(view.id, {
      onSuccess: () => {
        if (view.id === activeId) setActiveView(null);
      },
    });
  };

  const openManage = () => {
    void manageModal.show({ datasetKey });
  };

  return {
    views,
    myViews,
    sharedViews,
    activeView,
    isDirty,
    isLoading: listQuery.isLoading,
    isSaving: createMutation.isPending || updateMutation.isPending,
    apply,
    revert,
    unapply,
    saveAsNew,
    updateCurrent,
    remove,
    openManage,
    canManageView: isOwner,
  };
};
